/**
 * Display rules for the Missed Calls page (docs/MISSED_CALLS_PLAN.md §6). Pure functions, unit-tested in
 * __tests__/missedCallFormat.test.js. Times are shown in the office's zone (America/New_York).
 */
import { formatDuration, formatPhone, isDialableNumber, personName } from '../../voice/callFormat';
import { countryPhoneCodes } from '../weekly/studentButtonMenu/countryPhoneCode';

export const NY_ZONE = 'America/New_York';
const LIVE_INITIATED_MS = 90 * 1000; // same rule as the server's findLiveCallForAdmin
const WEEKDAYS_KO = ['일', '월', '화', '수', '목', '금', '토'];

export const FIELD_LABEL = { note: '내용', handoff: '담당자 연결', task_progress: '처리현황', memo: 'AI 요약 메모', who_color: 'Who 색상' };

/** A call's end in 기록 (CALL_RESULT). */
const CALL_RESULT_TEXT = { no_answer: '받지 않음', busy: '통화 중이라 연결 안 됨', failed: '통화 실패', canceled: '통화 취소' };

/**
 * 담당자 연결 as the cell shows it: the admin the row is handed to (first name, like 담당자), or — on a row written before
 * it became a dropdown — the text typed then, marked `legacy` so it reads as not yet picked. → { text, legacy }
 */
export const handoffLabel = (row) => {
    const to = row?.handoff_to;
    if (to) return { text: to.first_name || personName(to) || `#${to.id}`, legacy: false };
    const typed = String(row?.handoff || '').trim();
    return { text: typed, legacy: Boolean(typed) };
};
export const WHO_LABEL = { student: '학생', parent: '학부모', parent_2: '학부모2', teacher: '선생님' };

// Newer ICU puts a narrow no-break space before AM/PM; the page (and the tests) use a plain one.
const plainSpaces = (s) => s.replace(/[  ]/g, ' ');
const clockFmt = new Intl.DateTimeFormat('en-US', { timeZone: NY_ZONE, hour: 'numeric', minute: '2-digit' });
const dayFmt = new Intl.DateTimeFormat('en-US', { timeZone: NY_ZONE, month: 'numeric', day: 'numeric' });

/** "2026-09-11" → "9/11 (목)". */
export const formatListDate = (listDate) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(listDate || ''));
    if (!m) return '';
    const weekday = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
    return `${Number(m[2])}/${Number(m[3])} (${WEEKDAYS_KO[weekday]})`;
};

const isoDayFmt = new Intl.DateTimeFormat('en-CA', { timeZone: NY_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' });
/** Today in the office's zone as "YYYY-MM-DD" — the day the server calls today. */
export const nyTodayISO = (now = new Date()) => isoDayFmt.format(now);

/** "2026-09-15", -1 → "2026-09-14" (calendar days). '' for anything that isn't a date. */
export const shiftISODate = (iso, days) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    if (!m) return '';
    return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days)).toISOString().slice(0, 10);
};
const daysBetween = (from, to) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000);

/**
 * The lists an upload or typed-in rows can go under (plan §3.9): today and the `pastDays` before it — Sunday's
 * missed calls are entered on Monday. → [{ date, label }] newest first: "오늘 9/15 (화)", "어제 9/14 (월)", "9/13 (일)"…
 */
export const listDayChoices = (today, pastDays = 6) => {
    if (!shiftISODate(today, 0)) return [];
    return Array.from({ length: pastDays + 1 }, (_, i) => {
        const date = shiftISODate(today, -i);
        return { date, label: `${i === 0 ? '오늘 ' : i === 1 ? '어제 ' : ''}${formatListDate(date)}` };
    });
};

/** "오늘", or "9/14 (일)" for a past day's list. */
export const dayName = (listDate, today) => (!listDate || !today || listDate === today ? '오늘' : formatListDate(listDate));

/** "오늘", or "9/14 (일) 목록에" — for "… 이미 있는 번호가 N건 있습니다". */
export const dayPhrase = (listDate, today) => (dayName(listDate, today) === '오늘' ? '오늘' : `${formatListDate(listDate)} 목록에`);

/**
 * What an upload takes from today's Recents screenshots for `listDate`: today's calls, which show a time, or a past
 * day's — the ones the iPhone labels 어제 or with the weekday, which show none.
 */
export const dayUploadHint = (listDate, today) => {
    // One day or several (plan §3.9) — the uploader ticks the days to keep and everything else is dropped.
    const days = (Array.isArray(listDate) ? listDate : [listDate]).filter(Boolean).sort();
    if (days.length > 1) {
        const names = days.map((d) => formatListDate(d)).join(', ');
        const past = today ? days.filter((d) => d < today) : [];
        return `오늘 찍은 최근 통화 화면에서 ${names} 통화만 각 날짜 목록에 넣습니다. 선택하지 않은 날짜의 통화는 제외됩니다.${past.length ? ' iPhone은 지난 통화의 시간을 보여 주지 않아 "시간 모름"으로 저장됩니다 (알면 입력하세요).' : ''}`;
    }
    const one = days[0] || null;
    const back = one && today ? daysBetween(one, today) : 0;
    if (back <= 0) return '오늘 찍은 iPhone 최근 통화(부재중) 화면만 올려 주세요. "어제"나 날짜로 표시된 통화는 제외됩니다.';
    const label = back === 1 ? '어제' : `${WEEKDAYS_KO[new Date(`${one}T00:00:00Z`).getUTCDay()]}요일`;
    return `오늘 찍은 최근 통화 화면에서 "${label}"로 표시된 통화만 ${formatListDate(one)} 목록에 넣습니다. iPhone은 지난 통화의 시간을 보여 주지 않아 "시간 모름"으로 저장됩니다 (알면 입력하세요).`;
};

/** ISO → "9:41 AM" (New York). */
export const formatClock = (iso) => (iso ? plainSpaces(clockFmt.format(new Date(iso))) : '');

/** ISO → "14:05" in the office's zone — the value a <input type="time"> wants, not the browser's local clock. */
const inputClockFmt = new Intl.DateTimeFormat('en-GB', { timeZone: NY_ZONE, hour: '2-digit', minute: '2-digit', hour12: false });
export const clockValue = (iso) => (iso ? inputClockFmt.format(new Date(iso)) : '');

/** ISO → "9/11 4:12 PM" (New York). */
export const formatDateTime = (iso) => (iso ? `${dayFmt.format(new Date(iso))} ${formatClock(iso)}` : '');

/**
 * Text colour of a row comes only from the tasks on the AI summary
 * memo of the row's own call count, 신규문의 or 재문의 — the ones its 학생카드반영 badge shows:
 *   red     one of them is open
 *   blue    all of them are completed
 *   black   none, or no call yet — the ordinary case. Tasks elsewhere on the student's record don't count
 * A deleted row (the 삭제된 항목 view) is grey.
 */
export const rowTone = (row) => {
    if (row?.deleted_at) return 'deleted';
    if (row?.has_open_task) return 'red';
    if (row?.has_task) return 'blue';
    return 'default';
};

/**
 * "부재 9:41 AM (3)" — the most recent missed time and the iPhone's grouped count. `withDay` puts the row's 부재 day in
 * front ("부재 9/11 (목) 9:41 AM (3)") for the table, where the bold line above it is the upload day, not this one.
 */
export const missedLine = (row, { withDay = false } = {}) => {
    if (!row?.last_missed_at) return '';
    const count = row.missed_count > 1 ? ` (${row.missed_count})` : '';
    const day = withDay && formatListDate(row.list_date) ? `${formatListDate(row.list_date)} ` : '';
    // A past day's row (plan §3.9): the screenshot showed only the day.
    return row.missed_time_unknown ? `부재 ${day}시간 모름${count}` : `부재 ${day}${formatClock(row.last_missed_at)}${count}`;
};

/**
 * The 날짜 cell's bold line: the day the row was uploaded — first saved, by a confirmed screenshot import or typed in —
 * in the office's zone. A later upload merged into the row doesn't move it. Its 부재 day when that is unknown.
 */
export const uploadDateISO = (row) => {
    const at = row?.createdAt ? new Date(row.createdAt) : null;
    return row?.upload_date || (at && !Number.isNaN(at.getTime()) ? isoDayFmt.format(at) : row?.list_date);
};
export const uploadedDay = (row) => formatListDate(uploadDateISO(row));

const nameWithNumber = (s) => `${personName(s) || s.korean_name || ''}${s.consultation_number ? ` #${s.consultation_number}` : ''}`.trim();
const looksLikeNumber = (s) => /^[+\d\s().-]+$/.test(s);

/**
 * The Who cell as lines [{ text, student?, teacher? }]: from the call once one has a contact, else from the
 * import's number lookup. Siblings get a line each; a number nobody has → "모름" (+ a saved contact's name).
 */
export const whoLines = (row) => {
    const who = row?.who || {};
    if (who.teacher) return [{ text: `선생님 · ${personName(who.teacher)}`, teacher: who.teacher }];
    const students = Array.isArray(who.students) ? who.students : [];
    if (students.length) {
        return students.map((s, i) => {
            const label = WHO_LABEL[s.contact_label] || (i === 0 ? WHO_LABEL[who.label] : null);
            return { text: label ? `${label} · ${nameWithNumber(s)}` : `+ ${nameWithNumber(s)}`, student: s };
        });
    }
    const raw = String(row?.raw_label || '').trim();
    return [{ text: raw && !looksLikeNumber(raw) ? `모름 · ${raw}` : '모름' }];
};

/** Whether the row already names someone — a student or a teacher — whoever set it (an admin, the call, the import). */
export const hasAssignedWho = (row) => Boolean(row?.who?.teacher || (Array.isArray(row?.who?.students) && row.who.students.length));

/**
 * What 새 학생 만들기 would throw away on a row whose call already has a memo — or null when nothing is at stake.
 * Creating the student re-attaches the call to them; the server treats that as a change of student, deletes the old
 * memo (retractCallMemo) and its tasks go with it (FK cascade), then writes a new memo. A call only has a memo when
 * it is attached to a student, so a memo on the row means that deletion will happen.
 */
export const newStudentWarning = (row) => {
    const memo = row?.connected_call?.memo;
    if (!memo?.id) return null;
    const tasks = Array.isArray(memo.tasks) ? memo.tasks : [];
    const openInfoMail = tasks.filter((t) => t.is_info_mail && !t.is_completed).length;
    const what = tasks.length
        ? `이 통화의 메모와 태스크 ${tasks.length}개${openInfoMail ? `(열린 신규문의 ${openInfoMail}개 포함)` : ''}가`
        : '이 통화의 메모가';
    const edited = row.connected_call.summary_edited ? ' 메모 수정으로 고친 요약도 함께 사라집니다.' : '';
    return `새 학생을 만들면 ${what} 삭제되고, 새 학생에게 새 메모가 만들어집니다.${edited}`;
};

/**
 * Taking a row another admin holds — 담당하기 on a held row, or a manager choosing someone else from 담당 지정.
 * → null when nobody else holds it (nothing to confirm), else { from, since, onCall }: onCall when that admin is on
 * a call placed from this row right now (the same 90-second rule liveCallText uses).
 */
export const claimTakeover = (row, targetAdminId, now = Date.now()) => {
    const from = row?.claimed_by;
    if (!from || from.id == null || targetAdminId == null) return null;
    if (Number(from.id) === Number(targetAdminId)) return null;
    const onCall = Boolean(liveCallText(row, now)) && Number(row.live_call?.admin?.id) === Number(from.id);
    return { from, since: row.claimed_at || null, onCall };
};

/** A call from this row ringing or connected right now → "홍매 통화 중". A just-created call counts for 90 s only. */
export const liveCallText = (row, now = Date.now()) => {
    const live = row?.live_call;
    if (!live) return null;
    if (live.status === 'initiated' && now - new Date(live.createdAt).getTime() >= LIVE_INITIATED_MS) return null;
    return `${live.admin?.first_name || live.admin?.last_name || '누군가'} 통화 중`;
};

/** ms until an 'initiated' live call stops counting (for a re-render), or null. */
export const liveCallExpiresIn = (row, now = Date.now()) => {
    const live = row?.live_call;
    if (!live || live.status !== 'initiated') return null;
    return Math.max(0, new Date(live.createdAt).getTime() + LIVE_INITIATED_MS - now);
};

/** No call got through yet, but some were tried → "부재 2회 · 4:10 PM". */
export const attemptsText = (row) => {
    const a = row?.attempts;
    if (!a || row?.connected_call || !a.unanswered) return null;
    return `부재 ${a.unanswered}회${a.last_at ? ` · ${formatClock(a.last_at)}` : ''}`;
};

/**
 * AI 요약: the call's summary, "요약 중…" only while one is actually coming, '' when there is none.
 * A call nobody spoke on transcribes to an empty string and COMPLETED — there is nothing to summarise, so the
 * cell must not sit on "요약 중…" forever waiting for it.
 */
/** The AI 요약 cell's note for a call that got through but whose transcript came back empty — no summary is coming. */
export const NO_SPEECH_TEXT = '인식된 말이 없습니다';

/** Whether the cell shows NO_SPEECH_TEXT rather than a summary: the call was transcribed and nothing was recognised. */
export const noSpeechRecognized = (row) => {
    const c = row?.connected_call;
    return Boolean(c && !c.summary && !(c.summary_edited && c.memo_summary) && c.transcript_status === 'COMPLETED' && c.has_transcript === false);
};

export const summaryText = (row) => {
    const c = row?.connected_call;
    if (!c) return '';
    // An admin's correction (메모 수정 → the student's memo) wins; the AI's original stays one click away.
    if (c.summary_edited && c.memo_summary) return c.memo_summary;
    if (c.summary) return c.summary;
    if (c.transcript_status === 'COMPLETED') return c.has_transcript === false ? NO_SPEECH_TEXT : '요약 중…';
    return ['PENDING', 'PROCESSING'].includes(c.transcript_status) ? '요약 중…' : '';
};

/** Whether the cell is showing an admin's correction rather than the AI's text — the memo the call wrote was edited. */
export const summaryEdited = (row) => Boolean(row?.connected_call?.summary_edited && row.connected_call.memo_summary);

/** The AI's original summary, for 'AI 원문 보기' on an edited cell. */
export const originalSummaryText = (row) => row?.connected_call?.summary || '';

/** The task the badge acts on: the first still open, else the latest. */
export const currentTask = (tasks) => {
    const list = Array.isArray(tasks) ? tasks : [];
    return list.find((t) => !t.is_completed) || list[list.length - 1] || null;
};

/** Same colors as the consultation card: open 신규문의 → error (red), open 재문의 → warning (orange), none / done → disabled. */
export const taskBadgeColor = (tasks) => {
    const list = Array.isArray(tasks) ? tasks : [];
    if (list.some((t) => !t.is_completed && t.is_info_mail)) return 'error';
    if (list.some((t) => !t.is_completed)) return 'warning';
    return 'disabled';
};

// ── Filters and the live list ───────────────────────────────────────────────────────────────────
export const DEFAULT_FILTERS = { handled: '', from: '', to: '', claimed_by: '', handoff_to: '', who: '', who_red: false, q: '', deleted: false };

/** Filters → the list query (empty values left out). */
export const filtersToParams = (f = DEFAULT_FILTERS) => {
    const p = {};
    if (f.handled === 'false' || f.handled === 'true') p.handled = f.handled;
    if (f.from) p.from = f.from;
    if (f.to) p.to = f.to;
    if (f.claimed_by) p.claimed_by = f.claimed_by;
    if (f.handoff_to) p.handoff_to = f.handoff_to;
    if (f.who) p.who = f.who;
    if (f.who_red) p.who_red = 'true';
    if (f.q && String(f.q).trim()) p.q = String(f.q).trim();
    if (f.deleted) p.deleted = 'true';
    return p;
};

/** URL query → filters (unknown values ignored). */
export const paramsToFilters = (params) => {
    const read = (k) => (typeof params?.get === 'function' ? params.get(k) : params?.[k]) || '';
    return {
        handled: ['true', 'false'].includes(read('handled')) ? read('handled') : '',
        from: /^\d{4}-\d{2}-\d{2}$/.test(read('from')) ? read('from') : '',
        to: /^\d{4}-\d{2}-\d{2}$/.test(read('to')) ? read('to') : '',
        claimed_by: /^(me|none|\d+)$/.test(read('claimed_by')) ? read('claimed_by') : '',
        handoff_to: /^(me|none|\d+)$/.test(read('handoff_to')) ? read('handoff_to') : '',
        who: ['student', 'parent', 'teacher', 'unknown'].includes(read('who')) ? read('who') : '',
        who_red: read('who_red') === 'true',
        q: read('q'),
        deleted: read('deleted') === 'true',
    };
};

/** Does a live update still belong in the current view? Mirrors the server's filters closely enough for the page. */
export const rowMatchesFilters = (row, f = DEFAULT_FILTERS, meId = null) => {
    if (Boolean(row.deleted_at) !== Boolean(f.deleted)) return false;
    if (f.handled === 'false' && row.is_handled) return false;
    if (f.handled === 'true' && !row.is_handled) return false;
    if (f.from && row.list_date < f.from) return false;
    if (f.to && row.list_date > f.to) return false;
    if (f.claimed_by) {
        const owner = row.claimed_by?.id ?? null;
        if (f.claimed_by === 'me' && owner !== meId) return false;
        if (f.claimed_by === 'none' && owner !== null) return false;
        if (/^\d+$/.test(f.claimed_by) && owner !== Number(f.claimed_by)) return false;
    }
    if (f.handoff_to) {
        const handoff = row.handoff_to?.id ?? null;
        if (f.handoff_to === 'me' && handoff !== meId) return false;
        if (f.handoff_to === 'none' && handoff !== null) return false;
        if (/^\d+$/.test(f.handoff_to) && handoff !== Number(f.handoff_to)) return false;
    }
    if (f.who) {
        const who = row.who || {};
        const anyone = Boolean(who.teacher) || (who.students || []).length > 0;
        const kind = who.teacher ? 'teacher' : who.label === 'parent_2' ? 'parent' : who.label;
        if (f.who === 'unknown' ? anyone : kind !== f.who) return false;
    }
    if (f.who_red && !row.who_is_red) return false;
    const q = String(f.q || '').trim();
    if (q) {
        const digits = q.replace(/\D/g, '');
        if (digits.length >= 3 && looksLikeNumber(q)) {
            if (!String(row.phone_number || '').includes(digits)) return false;
        } else if (!`${row.raw_label || ''}\n${row.note || ''}`.toLowerCase().includes(q.toLowerCase())) {
            return false;
        }
    }
    return true;
};

/** Match the server's list order: newest upload day, then 번호 and id. */
export const compareRows = (a, b) => {
    const dayA = uploadDateISO(a), dayB = uploadDateISO(b);
    if (dayA !== dayB) return dayA < dayB ? 1 : -1;
    return a.daily_seq - b.daily_seq || a.id - b.id;
};

/** Insert or replace a row, keeping the list order. */
export const upsertSorted = (list, row) => {
    const rest = list.filter((r) => r.id !== row.id);
    const at = rest.findIndex((r) => compareRows(row, r) < 0);
    if (at === -1) rest.push(row);
    else rest.splice(at, 0, row);
    return rest;
};

/** Would a new row fall inside what is loaded so far? Past the last loaded row it arrives by scrolling instead. */
export const withinLoaded = (row, loaded, hasMore) => {
    if (!hasMore || !loaded.length) return true;
    return compareRows(row, loaded[loaded.length - 1]) <= 0;
};

// ── History and texts ───────────────────────────────────────────────────────────────────────────
/** One history line (EDITED is shown with its old and new text by the dialog). */
export const eventText = (e) => {
    switch (e?.event) {
        case 'IMPORTED': return '스크린샷에서 추가';
        case 'ADDED': return '번호로 직접 추가';
        case 'MERGED': return '같은 번호가 다시 부재중 (시간 갱신)';
        case 'REOPENED': return '새 부재중 → 다시 미처리';
        case 'EDITED': return `${FIELD_LABEL[e.field] || e.field} 수정`;
        case 'CLAIMED': return `담당 지정${e.meta?.to_admin_name ? `: ${e.meta.to_admin_name}` : ''}`;
        case 'UNCLAIMED': return '담당 해제';
        case 'WHO_SET': return `${e.meta?.new_student ? '새 학생 만들어 ' : ''}Who 지정${e.meta?.names?.length ? `: ${e.meta.names.join(', ')}` : e.meta?.name ? `: ${e.meta.name}` : ''}`;
        case 'WHO_CLEARED': return 'Who 지정 해제';
        // meta.from_admin_id is there only when the call made the caller 담당자
        case 'CALL_PLACED': return `전화 걸기${e.meta && Object.prototype.hasOwnProperty.call(e.meta, 'from_admin_id') ? ' (담당자로 지정됨)' : ''}`;
        case 'CALL_RESULT': return `통화 결과: ${e.meta?.status === 'completed'
            ? `연결됨${Number.isFinite(e.meta?.duration_seconds) ? ` (${formatDuration(e.meta.duration_seconds)})` : ''}`
            : (CALL_RESULT_TEXT[e.meta?.status] || e.meta?.status || '')}`;
        case 'SUMMARY_READY': return 'AI 요약 도착';
        case 'CALL_CONTACT_CHANGED': return `통화 대상 변경: ${e.meta?.from || '없음'} → ${e.meta?.to || '없음'}${e.meta?.memo_removed ? ' (이전 AI 요약 메모와 태스크 삭제)' : ''}`;
        case 'MEMO_DELETED': return 'AI 요약 메모 삭제 (태스크 포함)';
        case 'HANDLED': return '완료';
        case 'UNHANDLED': return '완료 취소';
        case 'SMS_SENT': return `문자 보냄${e.meta?.redirected_to ? ` (개발 환경: ${formatPhone(e.meta.redirected_to)}로)` : ''}`;
        case 'SMS_FAILED': return `문자 실패${e.meta?.error ? `: ${e.meta.error}` : ''}`;
        case 'SMS_SKIPPED_DEV': return '문자 (개발 환경: 발송 안 됨)';
        case 'DELETED': return '삭제';
        // A task on the AI summary memo of this row's call, changed on any page — the badge, 상담진행상황, Task Dashboard
        case 'TASK_CREATED': return `태스크 생성 (${e.meta?.is_info_mail ? '신규문의' : '재문의'})`;
        case 'TASK_COMPLETED': return `태스크 완료${e.meta?.via === 'info_mail' ? ' (안내 메일 발송)' : ''}`;
        case 'TASK_REOPENED': return '태스크 완료 취소';
        case 'TASK_CLAIMED': return '태스크 담당 지정';
        case 'TASK_UNCLAIMED': return '태스크 담당 해제';
        default: return e?.event || '';
    }
};

/** SMS parts a text costs: Korean (UCS-2) 70, then 67 per part; plain text 160, then 153. */
export const smsSegments = (text) => {
    const s = String(text || '');
    if (!s) return 0;
    const unicode = /[^ -~\n\r]/.test(s);
    const single = unicode ? 70 : 160;
    const part = unicode ? 67 : 153;
    return s.length <= single ? 1 : Math.ceil(s.length / part);
};

/**
 * The text dialog's counter, in words anyone can read. The count always shows; the cost line appears only once a text
 * is billed as more than one SMS — and says the parent still sees one message, because phones join the parts back up.
 * → { count: "97자 / 최대 500자", cost: "문자 요금 2건 (받는 분께는 한 통으로 보여요)" | null }
 */
export const smsLengthText = (text, maxLength) => {
    const length = String(text || '').length;
    const segments = smsSegments(text);
    return {
        count: `${length}자 / 최대 ${maxLength}자`,
        cost: segments > 1 ? `문자 요금 ${segments}건 (받는 분께는 한 통으로 보여요)` : null,
    };
};

// ── Who search by phone number ──────────────────────────────────────────────────────────────────
export const MIN_PHONE_DIGITS = 4;   // the server's limit too: fewer would match half the school

/** The digits a phone search sends: formatting ignored, a leading US 1 dropped (numbers are stored without it). */
export const phoneSearchDigits = (q) => {
    const d = String(q || '').replace(/\D/g, '');
    return d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
};

/** Which of a person's numbers a phone search matched, in words. */
export const MATCHED_NUMBER_LABEL = { student: '학생 번호', parent: '학부모 번호', parent_2: '학부모2 번호', teacher: '선생님 번호' };

/** Typed into the name search but almost certainly a phone number: only phone characters, and 7+ digits. */
export const looksLikePhoneQuery = (q) => /^[\d\s()+.-]+$/.test(String(q || '').trim()) && phoneSearchDigits(q).length >= 7;

// ── Screenshot review ───────────────────────────────────────────────────────────────────────────
export const FLAG_TEXT = {
    needs_number: '번호 입력 필요 (저장된 연락처)',
    unclear_digits: '확인 필요 (숫자가 흐림)',
    no_caller_id: '발신자 정보 없음',
    not_today: '오늘 아님',
    other_day: '다른 날',
    time_unknown: '시간 모름',
    compare_mismatch: '확인 필요 (두 판독이 다름)',
    compare_only: '확인 필요 (두 번째 판독에만 있음)',
};

/** Notes shown under a candidate in the review. The same-day collision is a choice, not a note — see ON_DUPLICATE. */
export const candidateNotes = (c) => {
    const notes = (c?.flags || []).map((f) => FLAG_TEXT[f] || f);
    if (c?.open_elsewhere > 0) notes.push(`다른 날짜에 미처리 ${c.open_elsewhere}건`);
    return notes;
};

/**
 * What to do with a number already on today's list. 합치기 is the default and what the page did before the
 * choice existed: the same caller about the same thing. 새 행 is for a second, unrelated call — it gets its own
 * 번호 and its own 내용 / 담당자 / 처리현황. 무시 leaves the existing row exactly as it is, 완료 included.
 */
export const ON_DUPLICATE = { MERGE: 'merge', NEW: 'new', SKIP: 'skip' };
export const ON_DUPLICATE_CHOICES = [
    { value: ON_DUPLICATE.MERGE, label: '합치기' },
    { value: ON_DUPLICATE.NEW, label: '새 행' },
    { value: ON_DUPLICATE.SKIP, label: '무시' },
];

/** What the row already on the day looks like: "오늘 #3에 있음 (완료)", or null when there is no collision. */
export const duplicateNote = (c) => {
    const m = c?.merge_into;
    if (!m) return null;
    const more = m.rows_today > 1 ? ` 외 ${m.rows_today - 1}건` : '';
    return `${dayName(m.upload_date || m.list_date, nyTodayISO())} #${m.daily_seq}${more}에 있음${m.is_handled ? ' (완료)' : ''}`;
};

/** "#8" → "#8은", "#12" → "#12는": the topic particle a number takes read aloud — only its last digit's sound matters. */
export const withTopic = (n) => `${n}${/[2459]$/.test(String(n)) ? '는' : '은'}`;

/**
 * All three answers spelled out for this number's row on today's list, shown under the picker before one is chosen —
 * the office's wording. 합치기 targets the latest row for the number (merge_into).
 */
export const duplicateChoices = (c, { timeUnknown = false } = {}) => {
    const m = c?.merge_into;
    if (!m) return [];
    const seq = `#${m.daily_seq}`;
    // 합치기 promises only what saving does (plan §3.9). On today's list a newer time can reopen a row ticked 완료. On a
    // past day's list an entry with no time only adds to the count, and a typed time is almost never after the 완료
    // tick, so the reopen isn't promised there.
    const pastDay = Boolean(m.list_date && m.list_date !== nyTodayISO());
    const merge = !pastDay
        ? `${seq}의 시간·횟수를 갱신 (완료였으면 다시 미처리)`
        : (timeUnknown ? `${seq}의 횟수만 갱신 (시간을 몰라 시간은 그대로)` : `${seq}의 시간·횟수를 갱신`);
    return [
        { value: ON_DUPLICATE.MERGE, label: '합치기', text: merge },
        { value: ON_DUPLICATE.NEW, label: '새 행', text: `새 번호로 따로 추가, ${withTopic(seq)} 그대로` },
        { value: ON_DUPLICATE.SKIP, label: '무시', text: `저장하지 않음, ${withTopic(seq)} 그대로` },
    ];
};

/** A candidate from the server → an editable review row. */
export const toReviewRow = (c) => ({
    key: c.key,
    include: Boolean(c.include),
    // The day this row goes under. One upload can span several (plan §3.9), so it travels with the row rather
    // than being one value for the whole import.
    list_date: c.list_date || null,
    phone_number: c.phone_number ? formatPhone(c.phone_number) : '',
    raw_label: c.raw_label || '',
    missed_time: c.missed_time || '',
    missed_count: c.missed_count || 1,
    // No choice yet when this number is already on today's list: the reviewer has to pick one (reviewRowError).
    on_duplicate: c.merge_into ? null : ON_DUPLICATE.MERGE,
    candidate: c,
});

/** An empty review row the admin adds by hand. */
export const blankReviewRow = (key, listDate = null) => ({
    key, include: true, list_date: listDate, phone_number: '', raw_label: '', missed_time: '', missed_count: 1,
    on_duplicate: ON_DUPLICATE.MERGE, candidate: null,
});

/** National digits, so a typed "(201) 555-0101" and the server's "+12015550101" compare equal. */
const numberDigits = (value) => {
    const d = String(value || '').replace(/\D/g, '');
    return d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
};
export const sameNumber = (a, b) => Boolean(numberDigits(a)) && numberDigits(a) === numberDigits(b);

/** Why a ticked review row can't be saved yet, or null. */
export const reviewRowError = (r, { timeOptional = false, dayRequired = false } = {}) => {
    if (!r?.include) return null;
    if (!isDialableNumber(r.phone_number)) return '번호를 입력하세요';
    // A multi-day upload has to say which list each row joins — a 다른 날 candidate carries no day, and the server
    // refuses the whole save rather than file it under the wrong one. Asked before the time: the day decides
    // whether a blank time is allowed.
    if (dayRequired && !r.list_date) return '날짜를 고르세요';
    // A past day's row may stay blank — the iPhone showed no time, and it is saved as 시간 모름 (plan §3.9).
    if (!(timeOptional && !r.missed_time) && !/^([01]?\d|2[0-3]):[0-5]\d$/.test(String(r.missed_time || ''))) return '시간을 입력하세요';
    // A number already on today's list is never saved on a default — the reviewer says which of the three.
    if (r.candidate?.merge_into && !r.on_duplicate) return '오늘 같은 번호가 있습니다 — 합치기 / 새 행 / 무시 중 하나를 고르세요';
    return null;
};

/** A review row → what confirm sends; null when it is unticked. */
export const toConfirmRow = (r) => (r?.include ? {
    key: r.key,
    // Which day's list this row belongs to — without it the server files every row under the upload's earliest
    // day, which would put today's calls on yesterday's list.
    ...(r.list_date ? { list_date: r.list_date } : {}),
    phone_number: r.phone_number,
    missed_time: r.missed_time,
    missed_count: Math.max(1, Number.parseInt(r.missed_count, 10) || 1),
    raw_label: r.raw_label || null,
    on_duplicate: r.on_duplicate || ON_DUPLICATE.MERGE,   // never reached for a colliding row — reviewRowError gates it
    // The row this number was shown as already on (null: none) and the number it was shown for. The server compares
    // that with what is being saved — an edited number is held to nothing — and refuses when today's list has changed
    // in the meantime (409 conflicts → applyDuplicateConflicts).
    merge_into_id: r.candidate?.merge_into?.id ?? null,
    ...(r.candidate?.phone_number ? { candidate_phone_number: r.candidate.phone_number } : {}),
    // Only sent once something has been dropped; the server keeps everything it found otherwise.
    ...(r.dropped_matches?.length ? { match_keys: keptMatches(r).map(matchKey) } : {}),
} : null);

export const DUPLICATE_CONFLICT_TEXT = '그 사이 같은 번호가 오늘 목록에 추가되었습니다 — 표시된 번호의 합치기 / 새 행 / 무시를 다시 골라 주세요';

/** The numbers a save was refused for because today's list changed under the review, or null for any other error. */
export const duplicateConflictsOf = (err) => {
    const data = err?.response?.data;
    return err?.response?.status === 409 && Array.isArray(data?.conflicts) && data.conflicts.length ? data.conflicts : null;
};

/**
 * Put what the list looks like now on each row the server named, and clear its choice, so the reviewer answers
 * 합치기 / 새 행 / 무시 again before 저장 — nothing was saved.
 */
export const applyDuplicateConflicts = (rows, conflicts = []) => rows.map((r) => {
    // Matched by the row keys the server names (a typed number can read differently from the saved one); by number
    // only for a refusal that names none.
    const hit = r.include && conflicts.find((c) => (Array.isArray(c.keys) && c.keys.length ? c.keys.includes(r.key) : sameNumber(r.phone_number, c.phone_number)));
    if (!hit) return r;
    const base = sameNumber(r.candidate?.phone_number, hit.phone_number) ? r.candidate : { matches: [] };
    // The refusal names the day it was refused on, but the row it rebuilds carries no day of its own — without
    // this, duplicateNote/duplicateChoices fall back to today and a Sunday duplicate would be re-answered as
    // "오늘 #3에 있음", promising a reopen that saving will not do.
    // Only when a day is actually known: a single-day upload names none, and an invented `list_date: null` would
    // say something the refusal did not.
    const day = hit.merge_into?.list_date || hit.list_date || r.list_date || null;
    const mergeInto = hit.merge_into && day ? { ...hit.merge_into, list_date: day } : hit.merge_into;
    return { ...r, candidate: { ...base, phone_number: hit.phone_number, merge_into: mergeInto }, on_duplicate: null };
});

// ── Rows typed in by hand ───────────────────────────────────────────────────────────────────────
/** "17:36" — right now in the office's zone, the default missed time for a row an admin adds. */
export const nyNowHHmm = (now = new Date()) => inputClockFmt.format(now);

/**
 * A pasted block → one draft row per line. Admins paste from a note or a message, so anything that isn't a
 * number is kept as typed for them to fix rather than dropped silently.
 */
export const parseNumberBlock = (text, now = new Date()) => String(text || '')
    .split(/[\n,;]+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, i) => ({
        key: `typed-${Date.now()}-${i}`,
        include: true,
        phone_number: line,
        raw_label: '',
        missed_time: nyNowHHmm(now),
        missed_count: 1,
        on_duplicate: ON_DUPLICATE.MERGE,
        candidate: null,
    }));

/** A draft row → what the preview endpoint needs (it only looks numbers up). */
export const toPreviewRow = (r) => ({ phone_number: r.phone_number, missed_time: r.missed_time });

/** How a match is named between the page and the server: "student:12", "teacher:9". */
export const matchKey = (m) => `${m.kind}:${m.id}`;

/**
 * The people found on a number, minus the ones the reviewer dropped. A number on several records — siblings
 * sharing a parent's phone — matches them all, and only the admin knows which one actually rang.
 */
export const keptMatches = (row) => {
    const all = row?.candidate?.matches || [];
    const dropped = row?.dropped_matches;
    return dropped ? all.filter((m) => !dropped.includes(matchKey(m))) : all;
};

// ── A new student from a row ────────────────────────────────────────────────────────────────────
/** The office's own country code, the default for a new student card. */
export const US_PHONE_CODE = countryPhoneCodes.find((c) => c.name === 'United States');

/**
 * "+12015550101" → the country from the list plus the rest, so the number lands on the card the way the forms
 * store it. Longest code wins; +1 is shared by the US and Canada (and the list has Canada first), so the US
 * takes the tie — the office's own country.
 */
export const splitE164 = (e164) => {
    const raw = String(e164 || '').trim();
    if (!raw.startsWith('+')) return { country: US_PHONE_CODE, phone: raw.replace(/\D/g, '') };
    const matches = countryPhoneCodes.filter((c) => raw.startsWith(c.code));
    if (!matches.length) return { country: US_PHONE_CODE, phone: raw.replace(/\D/g, '').replace(/^1/, '') };
    const longest = Math.max(...matches.map((c) => c.code.length));
    const country = matches.filter((c) => c.code.length === longest).find((c) => c === US_PHONE_CODE) || matches.find((c) => c.code.length === longest);
    return { country, phone: raw.slice(country.code.length).replace(/\D/g, '') };
};
